import { NavLink } from 'react-router-dom';
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useRef, useState, PointerEvent } from 'react';
import styled, { css } from 'styled-components';
import { priceListRoutePermission } from 'constants/price-list';
import {
  ADMIN_PERMISSION,
  REPORTS_PERMISSIONS,
  RING_ORDERS_PERMISSIONS
} from 'constants/permissions';
import { PRICE_REPORTS, priceReportRoute } from 'constants/price-reports';
import {
  BackofficePermission,
  hasBackofficePermission,
  useBackofficePermissions
} from 'hooks/useBackofficePermission';
import { useLocalStorage } from 'hooks/useLocalStorage';
import NavBarDropdown, { NavGroup, NavRoute } from './navBarDropdown';

// An admin page in the menu.
const adminRoute = (route: string, routeTitle: string): NavRoute => ({
  route,
  routeTitle,
  permission: ADMIN_PERMISSION
});

// The menu can be dragged wider by its right edge, so long titles fit on one line; the width is kept
// in local storage, and a double click on the edge brings back the default.
const DEFAULT_WIDTH = 155;
const MIN_WIDTH = 120;
const MAX_WIDTH = 500;
const WIDTH_STORAGE_KEY = 'navBarWidth';

const clampWidth = (width: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));

const Nav = styled.nav`
  position: relative;
  flex-shrink: 0;
  min-height: ${props => `calc(100vh - ${props.theme.headerHeight})`};
  height: 750.03px;
  background: #f2f2f2 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  opacity: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  &:first-child {
    height: 3rem;
  }
`;

const linkStyle = css`
  font-family: ${props => props.theme.fonts.semiBold};
  font-size: 12px;
  text-decoration: none;
  margin: 5px 0 0 5px;
  padding-bottom: 3px;
  @media (max-width: 1200px) {
    font-size: 12px;
    margin: 5px 0 0 5px;
    font-family: ${props => props.theme.fonts.semiBold};
  }
`;

const StyledLink = styled(NavLink)`
  ${linkStyle}
  color: ${props => props.theme.colors.black};
  cursor: pointer;
  :hover {
    color: ${props => props.theme.colors.red};
  }
  &.active {
    border-bottom-color: white;
  }
`;

// A page the user has no permission for: in the menu, greyed out and not a link.
const DisabledLink = styled.span`
  ${linkStyle}
  color: ${props => props.theme.colors.grey};
  cursor: not-allowed;
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  z-index: 1;
  :hover,
  &.dragging {
    background: #d6d9de;
  }
`;

const Separator = styled.div`
  height: 10px;
  width: 90%;
  margin-left: 5px;
  margin-bottom: 10px;
  border-bottom: 1px solid #d6d9de;
`;

export const NavBar = () => {
  const [savedWidth, saveWidth] = useLocalStorage(WIDTH_STORAGE_KEY, DEFAULT_WIDTH);
  const [width, setWidth] = useState<number>(() => clampWidth(Number(savedWidth) || DEFAULT_WIDTH));
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, width: 0 });

  const onResizeStart = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, width };
    setIsDragging(true);
  };
  const onResize = (e: PointerEvent<HTMLDivElement>) => {
    if (isDragging) {
      setWidth(clampWidth(dragStart.current.width + e.clientX - dragStart.current.x));
    }
  };
  const onResizeEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      saveWidth(width);
    }
  };
  const onResizeReset = () => {
    setWidth(DEFAULT_WIDTH);
    saveWidth(DEFAULT_WIDTH);
  };

  const permissions = useBackofficePermissions();
  // A menu item outside the dropdowns, greyed out without the permission, as the dropdowns' items are.
  const menuLink = (route: string, title: string, permission: BackofficePermission) =>
    hasBackofficePermission(permissions, permission) ? (
      <StyledLink to={route}>{title}</StyledLink>
    ) : (
      <DisabledLink title="You do not have permission to see this page.">{title}</DisabledLink>
    );

  // Every page shows in the menu, and the ones the user has no permission for are greyed out; the
  // permissions match the pages' routes and the BFF.
  const pricesRoutes: Array<NavRoute | NavGroup> = [
    {
      id: 'PriceList',
      title: 'Price List',
      routes: [
        { route: '/priceupload', routeTitle: 'Price Upload' },
        { route: '/pricelist/currencyrates', routeTitle: 'Currency Rates' },
        { route: '/pricelist/parcels', routeTitle: 'Parcels' },
        { route: '/pricelist/texts', routeTitle: 'Weekly Texts' },
        { route: '/pricelist/preview', routeTitle: 'Preview' },
        { route: '/pricelist/templates', routeTitle: 'Templates' },
        { route: '/pricelist/publish', routeTitle: 'Publish' }
      ].map(route => ({ ...route, permission: priceListRoutePermission(route.route) }))
    },
    {
      id: 'RapaportTrade',
      title: 'Rapaport Trade',
      routes: [
        {
          route: '/pricelist/rapnetlists',
          routeTitle: 'Rapaport Trade Price Lists',
          permission: priceListRoutePermission('/pricelist/rapnetlists')
        },
        { route: '/pricelist/rapi', routeTitle: 'RAPI', permission: REPORTS_PERMISSIONS },
        ...[
          { route: '/rapaporttradepricelist/texts', routeTitle: 'Weekly Texts' },
          { route: '/rapaporttradepricelist/preview', routeTitle: 'Preview' },
          { route: '/rapaporttradepricelist/publish', routeTitle: 'Publish' },
          { route: '/rapaporttradepricelist/templates', routeTitle: 'Templates' }
        ].map(route => ({ ...route, permission: priceListRoutePermission(route.route) }))
      ]
    }
  ];
  const Routes: Array<{ id: string; title: string; routes: Array<NavRoute | NavGroup> }> = [
    {
      id: 'Devices',
      title: 'Device Management',
      routes: [
        adminRoute('/devices', 'Devices'),
        adminRoute('/defaultsettings', 'Default Settings'),
        adminRoute('/usresettings', 'User Settings')
      ]
    },
    {
      id: 'Upload',
      title: 'Upload',
      routes: [
        adminRoute('/servicemonitor', 'Service Monitor'),
        adminRoute('/uploadmonitor', 'Upload Monitor'),
        adminRoute('/certrefresh', 'Cert Refresh'),
        adminRoute('/gemstonecolumnmapping', 'Gemstone Column Mapping'),
        adminRoute('/gemstonevaluemapping', 'Gemstone Value Mapping'),
        adminRoute('/gemstonemanagevalidvalues', 'Gemstone Values Management')
      ]
    },
    { id: 'Prices', title: 'Prices', routes: pricesRoutes },
    // The reports of the legacy OFC page Price Report Center spec A3, one menu item each.
    {
      id: 'PriceReports',
      title: 'Price Reports',
      routes: PRICE_REPORTS.map(report => ({
        route: priceReportRoute(report.slug),
        routeTitle: report.title,
        permission: REPORTS_PERMISSIONS
      }))
    },
    {
      id: 'Compliance',
      title: 'Compliance',
      routes: [
        adminRoute('/compliancecountryrequirements', 'Country Document Requirements'),
        adminRoute('/compliancedocumenttypes', 'Document Types')
      ]
    },
    {
      id: 'SearchLogs',
      title: 'Search Logs',
      routes: [
        adminRoute('/searchlogs', 'Logs'),
        adminRoute('/searchlogs/defaultsettings', 'Default Settings'),
        adminRoute('/searchlogs/usersettings', 'User Settings'),
        adminRoute('/searchlogs/tierrules', 'Tier Limit Rules'),
        adminRoute('/searchlogs/tieroverrides', 'Account Overrides'),
        adminRoute('/searchlogs/tiertransitions', 'Tier Transitions'),
        adminRoute('/searchlogs/accounttiers', 'Account Tiers'),
        adminRoute('/searchlogs/eventlog', 'Event Log')
      ]
    },
    {
      id: 'Diamonds',
      title: 'Diamonds',
      routes: [
        adminRoute('/deleteddiamonds', 'Deleted Diamonds'),
        adminRoute('/diamondsummary', 'Diamond Summary')
      ]
    },
    {
      id: 'Accounts',
      title: 'Accounts',
      routes: [adminRoute('/accountpayments', 'Payment History')]
    },
    {
      id: 'Members',
      title: 'Members',
      routes: [adminRoute('/memberratings', 'Member Ratings')]
    }
  ];

  return (
    <Nav style={{ width }}>
      {Routes.map(route => (
        <NavBarDropdown routes={route.routes} id={route.id} title={route.title} />
      ))}
      {menuLink('/sourceverification', 'Source Verification', ADMIN_PERMISSION)}
      <Separator />
      {menuLink('/ringorders', 'Ring Orders', RING_ORDERS_PERMISSIONS)}
      <ResizeHandle
        className={isDragging ? 'dragging' : ''}
        title="Drag to resize the menu, double click to reset"
        onPointerDown={onResizeStart}
        onPointerMove={onResize}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
        onDoubleClick={onResizeReset}
      />
    </Nav>
  );
};

export default NavBar;
